import {
  ActionIcon,
  Checkbox,
  Flex,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import { type UseFieldArrayReturn, useFormContext } from "react-hook-form";
import ConfirmationButton from "src/components/shared/ConfirmationButton/ConfirmationButton";
import MultiSelect from "src/components/shared/_FormInputs/MultiSelect";
import Select from "src/components/shared/_FormInputs/Select";
import { useIsOnline } from "src/hooks";
import { type ILogbookSignersForm } from "src/pages/Settings/AdvancedFeatures/LogbookSigners/LogbookSigners.types";

type Props = Omit<
  UseFieldArrayReturn<ILogbookSignersForm, "rows", "fieldId">,
  "append" | "remove"
> & {
  remove: (index: number, id?: number) => void;
  logbooks: Array<{ value: string; label: string }>;
  roles: Array<{ value: string; label: string }>;
};

const LogbookSignersForm = ({ fields, remove, logbooks, roles }: Props) => {
  const { register, control } = useFormContext<ILogbookSignersForm>();
  const isOnline = useIsOnline();
  const readOnly = !isOnline;

  const handleRemoveRow = (index: number, id?: number) => {
    remove(index, id);
  };
  if (fields.length === 0) {
    return (
      <Text c="dimmed" ta="center" mt={40}>
        No signer configurations. Click Add to create one.
      </Text>
    );
  }

  return (
    <Stack gap={16} mt={30} px={16}>
      <Table.ScrollContainer minWidth={900}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Logbook</Table.Th>
              <Table.Th>Signer Role</Table.Th>
              <Table.Th ta="center">Required</Table.Th>
              <Table.Th>Cosigner Role</Table.Th>
              <Table.Th ta="center">Required</Table.Th>
              {!readOnly && <Table.Th w={50} />}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {fields.map((field, rowIndex) => (
              <Table.Tr key={field.fieldId}>
                <Table.Td w={150}>
                  <TextInput
                    readOnly={readOnly}
                    required
                    {...register(`rows.${rowIndex}.name`, { required: true })}
                  />
                </Table.Td>
                <Table.Td w={150}>
                  <Select
                    name={`rows.${rowIndex}.lbid`}
                    control={control}
                    data={logbooks}
                    readOnly={readOnly}
                    required
                    searchable
                  />
                </Table.Td>
                <Table.Td w={300}>
                  <MultiSelect
                    name={`rows.${rowIndex}.pri`}
                    control={control}
                    data={roles}
                    readOnly={readOnly}
                    searchable
                  />
                </Table.Td>
                <Table.Td w={50}>
                  <Flex justify="center">
                    <Checkbox
                      readOnly={readOnly}
                      {...register(`rows.${rowIndex}.priMandatory`)}
                    />
                  </Flex>
                </Table.Td>
                <Table.Td w={300}>
                  <MultiSelect
                    name={`rows.${rowIndex}.sec`}
                    control={control}
                    data={roles}
                    readOnly={readOnly}
                    searchable
                  />
                </Table.Td>
                <Table.Td w={50}>
                  <Flex justify="center">
                    <Checkbox
                      readOnly={readOnly}
                      {...register(`rows.${rowIndex}.secMandatory`)}
                    />
                  </Flex>
                </Table.Td>
                {!readOnly && (
                  <Table.Td w={50}>
                    <ConfirmationButton
                      modalText="Are you sure you want to remove this signer configuration?"
                      onConfirm={() => handleRemoveRow(rowIndex, field.id)}
                      buttonProps={{
                        variant: "transparent",
                        style: { backgroundColor: "transparent", padding: 0 },
                      }}>
                      <ActionIcon color="red" variant="outline">
                        <IconTrash size={18} />
                      </ActionIcon>
                    </ConfirmationButton>
                  </Table.Td>
                )}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Stack>
  );
};

export default LogbookSignersForm;
